import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tab,
  Tabs,
  CircularProgress,
  Box,
} from "@mui/material";
import { type FC, type SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import { Summary } from "./Summary";
import { ChangeLog } from "./ChangeLog";
import { useWorkPacketsContext } from "../WorkPacketsContext";
import toast from "react-hot-toast";
import { DialogActionContext, useDialogActionStore } from "./DialogActionContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAccrualRemittances,
  getChargebackRemittances,
  getShortageRemittances,
  getWorkPacket,
} from "../queries/work-packets";
import { GenerateObarButton } from "../WorkPacketsGrid/columns/GenerateObarButton";
import { WorkPacketType } from "../WorkPacketType";
import { useFetchWorkPacketActivationStatus } from "../activation-status/useFetchActivationStatusInfo";
import { ActivationStatusBanner } from "../activation-status/ActivationStatusBanner";
import { SunsetBannerBody, SunsetBannerTitle } from "../activation-status/SunsetBannerText";
import { DeactivationBannerTitle, DeactivationBannerBody } from "../activation-status/DeactivationBannerText";
import { ExpiredBannerBody, ExpiredBannerTitle } from "../activation-status/ExpiredBannerText";
import { MigratedBannerBody, MigratedBannerTitle } from "../activation-status/MigratedBannerText";

interface DetailsPopupProps {
  workPacketId: string;
  open: boolean;
  onClose: () => void;
  onSuccessfulUpdate?: () => void;
}

enum TabIndex {
  Summary,
  ChangeLog,
}

const remittanceGetters = {
  [WorkPacketType.CHARGEBACKS]: getChargebackRemittances,
  [WorkPacketType.SHORTAGES]: getShortageRemittances,
  [WorkPacketType.ACCRUALS]: getAccrualRemittances,
};

export const WorkPacketDetailsPopup: FC<DetailsPopupProps> = ({ workPacketId, open, onClose, onSuccessfulUpdate }) => {
  const queryClient = useQueryClient();
  const { currentWorkPacketType } = useWorkPacketsContext();
  const [selectedTab, setSelectedTab] = useState<TabIndex>(TabIndex.Summary);
  const [forceLoadingScreen, setForceLoadingSpinner] = useState<boolean>(false);
  const formRef = useRef<any>(null);

  const dialogActionStore = useDialogActionStore();
  const { pendingDialogActions } = dialogActionStore;

  const query = useQuery({
    queryFn: getWorkPacket,
    queryKey: ["work-packets", currentWorkPacketType, "details", workPacketId],
    staleTime: 30000,
  });

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ["work-packets", currentWorkPacketType, "remittance", workPacketId],
      queryFn: remittanceGetters[currentWorkPacketType] as any, // TODO: fix typing here
      staleTime: 30000,
    });
  }, [currentWorkPacketType, queryClient, workPacketId]);

  const workPacket = query.data;
  const loading = forceLoadingScreen || query.isFetching;

  const attemptClose = useCallback(() => {
    if (!pendingDialogActions.size) return void onClose();
    toast.error(
      `Can't close dialog yet - ${Array.from(pendingDialogActions, ([_key, action]) => action.description).join(", ")}`,
    );
  }, [onClose, pendingDialogActions]);

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, newValue: number) => {
      if (!pendingDialogActions.size) return void setSelectedTab(newValue);
      toast.error(
        `Can't change tab - ${Array.from(pendingDialogActions, ([_key, action]) => action.description).join(", ")}`,
      );
    },
    [pendingDialogActions],
  );

  const activationStatus = useFetchWorkPacketActivationStatus(workPacket);

  if (!workPacket)
    return (
      <Dialog open={open} onClose={attemptClose} maxWidth="md" fullWidth>
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      </Dialog>
    );

  return (
    <DialogActionContext.Provider value={dialogActionStore}>
      <Dialog open={open} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle>Work Packet Details | {workPacket.recoveryStream}</DialogTitle>
        <DialogContent>
          {workPacket.migratedAt ? (
            <ActivationStatusBanner
              title={<MigratedBannerTitle />}
              date={workPacket.migratedAt.toDate()}
              body={<MigratedBannerBody />}
              isExpired
              sx={{ marginBottom: "0.5rem" }}
            />
          ) : activationStatus.state !== "active" ? (
            <ActivationStatusBanner
              title={
                activationStatus.state === "inactive" ? (
                  <DeactivationBannerTitle />
                ) : (
                  <SunsetBannerTitle scope={workPacket.recoveryStreamServer} />
                )
              }
              date={activationStatus.deactivationDate}
              body={activationStatus.state === "inactive" ? <DeactivationBannerBody /> : <SunsetBannerBody />}
              isDeactivation={activationStatus.state === "inactive"}
              sx={{ marginBottom: "0.5rem" }}
            />
          ) : workPacket.expiredAt ? (
            <ActivationStatusBanner
              title={<ExpiredBannerTitle />}
              date={workPacket.expiredAt ? workPacket.expiredAt.toDate() : undefined}
              body={<ExpiredBannerBody />}
              isExpired
              sx={{ marginBottom: "0.5rem" }}
            />
          ) : null}
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label="Summary" />
            <Tab label="Change Log" />
            {workPacket.workPacketType === WorkPacketType.ACCRUALS && (
              <GenerateObarButton workPacketId={workPacketId} sx={{ marginLeft: "auto", marginBlock: "0.75rem" }} />
            )}
          </Tabs>

          <Divider />

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {selectedTab === TabIndex.Summary && (
                <Summary
                  workPacket={workPacket}
                  formRef={formRef}
                  onSubmitSuccess={() => {
                    onClose();
                    onSuccessfulUpdate && onSuccessfulUpdate();
                  }}
                  setLoading={setForceLoadingSpinner}
                />
              )}
              {selectedTab === TabIndex.ChangeLog && (
                <ChangeLog workPacketId={workPacket.packetId} workPacketType={workPacket.workPacketType} />
              )}
            </>
          )}
        </DialogContent>

        {selectedTab === TabIndex.Summary && (
          <DialogActions>
            <Button className="contained-reversed" variant="contained" onClick={attemptClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading || !!workPacket.migratedAt}
              onClick={() => formRef.current.submitForm()}
            >
              <span>Save Changes</span>
              {loading && <CircularProgress size={14} style={{ marginLeft: "12px", color: "#101828" }} />}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </DialogActionContext.Provider>
  );
};
